import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { useAuth } from '../hooks/useAuth';
import SessionManager from '../utils/sessionManager';

const MyCoursesPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, role, isLoading: authLoading } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const candidateSession = SessionManager.getSession('candidate');

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) {
      return;
    }

    // Check authentication
    if (!isLoggedIn() || role !== 'candidate') {
      console.log('MyCoursesPage: Not authenticated, redirecting to login', { isLoggedIn: isLoggedIn(), role });
      navigate('/login');
      return;
    }

    // Load enrolled courses from API
    const loadEnrolledCourses = async () => {
      try {
        const token = candidateSession?.access_token;
        console.log('MyCoursesPage: Token details:', {
          hasToken: !!token,
          tokenLength: token?.length,
          tokenStart: token?.substring(0, 30),
          tokenEnd: token?.substring(token?.length - 30),
          candidateSession: candidateSession ? {
            hasAccessToken: !!candidateSession.access_token,
            userId: candidateSession.user_id,
            email: candidateSession.email,
            role: candidateSession.role
          } : null
        });
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/course-teaching/my-courses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setEnrolledCourses(data.courses || []);
        } else {
          console.error('Failed to fetch enrolled courses from backend');
          setEnrolledCourses([]);
        }
      } catch (error) {
        console.error('Error loading enrolled courses:', error);
        setEnrolledCourses([]);
      } finally {
        setLoading(false);
      }
    };

    loadEnrolledCourses();
  }, [navigate, isLoggedIn, role, authLoading]);

  const handleContinueLearning = (courseId) => {
    // Navigate to first module of the course
    const course = enrolledCourses.find(c => c.id === courseId);
    if (course && course.modules && course.modules.length > 0) {
      const firstModule = course.modules[0];
      navigate(`/course/${courseId}/module/${firstModule.id}/teach`);
    } else {
      // Fallback to course details if no modules
      navigate(`/courses/${courseId}`);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background-light dark:bg-background-dark">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-light dark:bg-background-dark">
      <Header />
      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="school" className="text-primary text-3xl" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Courses</h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              Continue your learning journey with your enrolled courses
            </p>
          </div>

          {/* Enrolled Courses */}
          {enrolledCourses.length === 0 ? (
            <div className="text-center py-16">
              <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                <Icon name="school" className="text-gray-400 text-5xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                No Courses Enrolled Yet
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start your learning journey by enrolling in courses
              </p>
              <Button onClick={() => navigate('/courses')}>
                Browse Courses
              </Button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {enrolledCourses.map((course) => (
                <div
                  key={course.id}
                  className="flex flex-col rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-lg border border-gray-200 dark:border-gray-800 hover:shadow-xl transition-shadow"
                >
                  {/* Course Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      {course.category}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-3 py-1 text-xs font-semibold text-green-700 dark:text-green-400">
                      Enrolled
                    </span>
                  </div>

                  {/* Course Title */}
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {course.title}
                  </h3>

                  {/* Course Info */}
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <span className="flex items-center gap-1">
                      <Icon name="schedule" size={16} />
                      {course.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="signal_cellular_alt" size={16} />
                      {course.level}
                    </span>
                  </div>

                  {/* Progress Bar (placeholder) */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-400">Progress</span>
                      <span className="font-semibold text-primary">0%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '0%' }}></div>
                    </div>
                  </div>

                  {/* Enrollment Date */}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Enrolled on {new Date(course.enrolledAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>

                  {/* Action Buttons */}
                  <div className="mt-auto space-y-2">
                    <Button
                      className="w-full"
                      onClick={() => handleContinueLearning(course.id)}
                    >
                      Continue Learning
                    </Button>
                    <button
                      onClick={() => navigate(`/courses/${course.id}`)}
                      className="w-full px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
                    >
                      View Course Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats Section (if courses enrolled) */}
          {enrolledCourses.length > 0 && (
            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              <div className="rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-lg border border-gray-200 dark:border-gray-800 text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  {enrolledCourses.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Courses Enrolled
                </div>
              </div>
              <div className="rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-lg border border-gray-200 dark:border-gray-800 text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  0
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Courses Completed
                </div>
              </div>
              <div className="rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-lg border border-gray-200 dark:border-gray-800 text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  0%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Average Progress
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyCoursesPage;
